import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, GraduationCap, Briefcase, MapPin, Clock, Layers } from "lucide-react";
import useSEO from "../hooks/useSEO";
import ParticleNetwork from "../components/ParticleNetwork";
import SectionHeading from "../components/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import CareerForm from "../components/careers/CareerForm";
import { SERVICE_CATEGORIES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollToApply = (e) => {
  e.preventDefault();
  const el = document.getElementById("apply");
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

const audiences = [
  {
    id: "early",
    icon: GraduationCap,
    overline: "Internships & Early Careers",
    title: "Students, fresh graduates & entry-level candidates",
    desc: "Start your professional journey with ATS IT Solutions. Students, fresh graduates and early-career candidates can submit their profiles for relevant internship and entry-level opportunities across our technology and digital domains.",
    cta: "Submit Your Profile",
    who: ["Students pursuing graduation", "Final-year & university students", "Recent graduates & freshers"],
  },
  {
    id: "pro",
    icon: Briefcase,
    overline: "Experienced Professionals",
    title: "Specialists, consultants & working professionals",
    desc: "Experienced professionals can share their profiles with ATS IT Solutions for relevant current and future opportunities across our technology, AI and digital services.",
    cta: "Share Your Resume",
    who: ["Experienced technology professionals", "Specialists & consultants", "Professionals exploring future opportunities"],
  },
];

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [selectedOpening, setSelectedOpening] = useState(null);
  useEffect(() => {
    axios.get(`${API}/careers/openings`).then((r) => setOpenings(r.data.items || [])).catch(() => setOpenings([]));
  }, []);
  const applyFor = (o) => (e) => {
    setSelectedOpening(o);
    scrollToApply(e);
  };
  useSEO({
    title: "Careers at ATS IT Solutions | Internships & Professional Opportunities",
    description:
      "Explore career opportunities at ATS IT Solutions. Students, freshers and experienced professionals can submit their profiles for relevant internship and professional opportunities.",
  });

  return (
    <div data-testid="careers-page">
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden bg-navy-deep">
        <div className="absolute inset-0 z-0 opacity-60"><ParticleNetwork className="w-full h-full" density={0.6} /></div>
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-orange/10 blur-[160px] z-0" />
        <div className="container-x relative z-10 text-center flex flex-col items-center">
          <Reveal><span className="overline">Careers at ATS IT Solutions</span></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-4 max-w-4xl">
              Build your career in <span className="text-gradient-orange">Technology, AI &amp; Digital Growth</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Whether you're a student starting your journey, a recent graduate looking for your first opportunity, or an experienced professional ready for your next challenge, explore opportunities with ATS IT Solutions.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
              <a href="#apply" onClick={scrollToApply} data-testid="careers-hero-cta" className="btn-primary text-base">Submit Your Profile <ArrowUpRight size={18} /></a>
              <a href="#opportunities" onClick={(e) => { e.preventDefault(); const el = document.getElementById("opportunities"); window.__lenis ? window.__lenis.scrollTo(el, { offset: -90 }) : el?.scrollIntoView({ behavior: "smooth" }); }} data-testid="careers-hero-secondary" className="btn-secondary text-base">Explore Opportunities <ArrowDown size={16} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="opportunities" data-testid="careers-audiences" className="relative py-20 md:py-28 bg-navy scroll-mt-24">
        <div className="container-x">
          <SectionHeading overline="Who we're looking for" title="Two paths, one talent database" subtitle="Tell us where you are in your career and we'll consider your profile for the opportunities that fit." />
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {audiences.map((a, i) => {
              const Icon = a.icon;
              return (
                <motion.div key={a.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.1 }} data-testid={`careers-audience-${a.id}`} className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden card-glow flex flex-col">
                  <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-orange/10 blur-3xl" />
                  <span className="overline">{a.overline}</span>
                  <div className="w-14 h-14 rounded-2xl bg-brand-orange/15 grid place-items-center mt-5"><Icon size={26} className="text-brand-orange" /></div>
                  <h3 className="font-head text-2xl md:text-3xl font-semibold text-white mt-5">{a.title}</h3>
                  <p className="text-slate-400 leading-relaxed mt-4">{a.desc}</p>
                  <ul className="mt-6 flex flex-wrap gap-2">
                    {a.who.map((w) => <li key={w} className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-200">{w}</li>)}
                  </ul>
                  <a href="#apply" onClick={scrollToApply} data-testid={`careers-audience-cta-${a.id}`} className="btn-primary mt-8 self-start">{a.cta} <ArrowUpRight size={16} /></a>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="openings" data-testid="careers-openings" className="relative py-20 md:py-28 bg-navy-deep border-y border-white/10">
        <div className="container-x">
          <SectionHeading overline="Current Openings" title={openings.length ? "Open roles at ATS" : "No verified openings right now"} subtitle={openings.length ? "Verified vacancies currently accepting applications." : "Don't see a suitable opening? Share your profile with us for consideration for relevant future opportunities."} />
          {openings.length > 0 ? (
            <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {openings.map((o) => (
                <StaggerItem key={o.id}>
                  <div data-testid="careers-opening-card" className="glass rounded-3xl p-7 h-full flex flex-col card-glow">
                    <span className="font-mono text-[10px] tracking-wider uppercase text-brand-orange">{o.domain}</span>
                    <h3 className="font-head text-xl font-semibold text-white mt-2">{o.title}</h3>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> {o.location}</span>
                      <span className="inline-flex items-center gap-1"><Layers size={12} /> {o.experience}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {o.employment_type}</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-4 leading-relaxed">{o.description}</p>
                    <div className="flex flex-wrap gap-2 mt-4">{o.skills.map((s) => <span key={s} className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-300">{s}</span>)}</div>
                    <a href="#apply" onClick={applyFor(o)} data-testid={`careers-opening-apply-${o.id}`} className="mt-auto pt-6 inline-flex items-center gap-2 text-brand-orange font-semibold text-sm hover:gap-3 transition-all">Apply <ArrowUpRight size={14} /></a>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          ) : (
            <Reveal delay={0.1}>
              <div className="mt-10 glass rounded-3xl p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="careers-no-openings">
                <div>
                  <p className="font-head text-xl text-white">General profile submission is open</p>
                  <p className="text-slate-400 text-sm mt-2 max-w-xl">We maintain a talent database across our service areas and reach out when a suitable opportunity matches a profile. Verified vacancies will be listed here when available.</p>
                </div>
                <a href="#apply" onClick={scrollToApply} data-testid="careers-no-openings-cta" className="btn-primary shrink-0 self-start md:self-auto">Submit Your Profile <ArrowUpRight size={16} /></a>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.15}>
            <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mt-14 mb-4">Opportunities may relate to these ATS domains</p>
            <div className="flex flex-wrap gap-2" data-testid="careers-domains">
              {SERVICE_CATEGORIES.map((c) => { const I = c.icon; return <span key={c.id} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-200"><I size={13} className="text-brand-orange" /> {c.title}</span>; })}
              <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-200">UI/UX Design</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="apply" data-testid="careers-apply" className="relative py-20 md:py-28 bg-navy scroll-mt-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8 glass rounded-3xl p-8 md:p-10">
            <h2 className="font-head text-2xl md:text-3xl font-semibold text-white mb-2">Submit your profile</h2>
            <p className="text-slate-400 text-sm mb-8">Fields marked * are required. The form adapts to how you're applying.</p>
            <CareerForm opening={selectedOpening} onClearOpening={() => setSelectedOpening(null)} />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <div className="glass rounded-3xl p-8 h-full">
              <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">How it works</p>
              <ol className="space-y-5">
                {["Select how you're applying — student, fresher or experienced professional.", "Complete the relevant details; upload a resume where applicable.", "Give consent for us to store your profile for recruitment.", "Our team reviews profiles and may reach out when an opportunity matches."].map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300"><span className="font-mono text-xs text-brand-orange mt-0.5">{String(i + 1).padStart(2, "0")}</span>{s}</li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-slate-500 leading-relaxed">
                <p>Resume rules: <span className="text-slate-300">optional</span> for students, <span className="text-slate-300">recommended</span> for freshers, <span className="text-slate-300">required</span> for experienced professionals.</p>
                <p className="mt-3">Your information is used only for recruitment and career-related purposes. Read our <Link to="/privacy" className="text-brand-orange hover:underline">Privacy Policy</Link>.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
